import { useMemo, useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Seo } from '../components/Seo';
import { ProductCard } from '../components/ProductCard';
import { categories, products } from '../data/products';
import type { ProductModel } from '../data/products';

type FilterState = {
  category: string[];
  axis: string[];
  accuracy: string[];
  interface: string[];
};

const EMPTY_FILTERS: FilterState = { category: [], axis: [], accuracy: [], interface: [] };

const AXIS_OPTIONS = [
  { value: '1', labelKey: 'products:axis.1' },
  { value: '2', labelKey: 'products:axis.2' },
  { value: '3', labelKey: 'products:axis.3' },
  { value: '6', labelKey: 'products:axis.6' }
];

const ACCURACY_OPTIONS = [
  { value: '<0.01', labelKey: 'products:accuracy.strategic' },
  { value: '0.01-0.1', labelKey: 'products:accuracy.navigation' },
  { value: '>0.1', labelKey: 'products:accuracy.tactical' }
];

const INTERFACE_OPTIONS = [
  { value: 'RS422', labelKey: 'products:interface.RS422' },
  { value: 'CAN', labelKey: 'products:interface.CAN' },
  { value: 'Ethernet', labelKey: 'products:interface.Ethernet' },
  { value: 'Analog', labelKey: 'products:interface.Analog' }
];

function matchAccuracy(acc: string, v: string): boolean {
  if (!acc) return false;
  if (v === '<0.01') return acc.indexOf('<0.01') !== -1;
  if (v === '0.01-0.1') return ['<0.1', '<0.05', '<0.03'].some((s) => acc.indexOf(s) !== -1);
  if (v === '>0.1') return ['<0.5', '<1', '<2'].some((s) => acc.indexOf(s) !== -1);
  return false;
}

function matches(p: ProductModel, filters: FilterState): boolean {
  if (filters.category.length && !filters.category.includes(p.category)) return false;
  if (filters.axis.length && !filters.axis.includes(String(p.axis))) return false;
  if (filters.accuracy.length && !filters.accuracy.some((v) => matchAccuracy(p.accuracy || '', v))) return false;
  if (filters.interface.length && !filters.interface.some((v) => (p.interface || '').indexOf(v) !== -1)) return false;
  return true;
}

export function Products() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [sortBy, setSortBy] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const toggle = (name: keyof FilterState, value: string) => {
    setFilters((prev) => {
      const list = prev[name];
      const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
      return { ...prev, [name]: next };
    });
  };

  const clearAll = () => {
    setFilters(EMPTY_FILTERS);
    setSortBy('');
  };

  const activeCount = Object.values(filters).reduce((sum, list) => sum + list.length, 0);

  const filtered = useMemo(() => {
    let list = products.filter((p) => matches(p, filters));
    if (sortBy === 'name-asc') list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === 'name-desc') list = [...list].sort((a, b) => b.name.localeCompare(a.name));
    return list;
  }, [filters, sortBy]);

  const filterGroup = (
    title: string,
    name: keyof FilterState,
    options: { value: string; labelKey: string }[]
  ) => (
    <div className="border-b border-slate-200 py-4">
      <h4 className="text-sm font-bold uppercase tracking-wide text-ink">{title}</h4>
      <div className="mt-3 space-y-2">
        {options.map((opt) => (
          <label key={opt.value} className="flex cursor-pointer items-center gap-2 text-sm text-slate-600 hover:text-ink">
            <input
              type="checkbox"
              checked={filters[name].includes(opt.value)}
              onChange={() => toggle(name, opt.value)}
              className="h-4 w-4 rounded border-slate-300 text-aqua focus:ring-aqua"
            />
            <span>{t(opt.labelKey)}</span>
          </label>
        ))}
      </div>
    </div>
  );

  const sidebar = (
    <aside className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-lg font-bold text-ink">
          <SlidersHorizontal size={18} /> {t('products:filters.title')}
        </h3>
        {activeCount > 0 ? (
          <button type="button" onClick={clearAll} className="text-sm font-semibold text-signal hover:underline">
            {t('products:filters.clearAll')}
          </button>
        ) : null}
      </div>

      {filterGroup(t('products:groups.category'), 'category', categories.map((c) => ({ value: c.slug, labelKey: `products:categories.${c.slug}.title` })))}
      {filterGroup(t('products:groups.axis'), 'axis', AXIS_OPTIONS)}
      {filterGroup(t('products:groups.accuracy'), 'accuracy', ACCURACY_OPTIONS)}
      {filterGroup(t('products:groups.interface'), 'interface', INTERFACE_OPTIONS)}

      <button type="button" onClick={clearAll} className="mt-4 w-full rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100">
        {t('products:filters.clearAllFilters')}
      </button>
    </aside>
  );

  return (
    <>
      <Seo
        title="Products - Fiber Optic Gyroscopes, Quartz Accelerometers, MEMS IMU & INS | GyroNavi"
        description="Browse GyroNavi's full product portfolio: fiber optic gyroscopes, quartz accelerometers, MEMS IMUs and inertial navigation systems from tactical to navigation grade."
        path="/products"
      />
      <section className="section">
        <p className="eyebrow">{t('products:page.eyebrow')}</p>
        <h1 className="mt-2 text-4xl font-black text-ink">{t('products:page.title')}</h1>
        <p className="mt-3 text-slate-600">{t('products:page.subtitle')}</p>

        <div className="mt-8 lg:grid lg:grid-cols-[280px_1fr] lg:gap-8">
          {/* 左侧过滤器（桌面） */}
          <div className="hidden lg:block">{sidebar}</div>

          {/* 产品列表 */}
          <div>
            <div className="flex items-center justify-between gap-3">
              <p className="text-slate-500">
                {t('products:filters.showing', { count: filtered.length, total: products.length })}
              </p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowFilters((v) => !v)}
                  className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 lg:hidden"
                >
                  <SlidersHorizontal size={16} /> {t('products:filters.title')}
                  {activeCount ? ` (${activeCount})` : ''}
                </button>
                <select
                  aria-label={t('products:sort.label')}
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700"
                >
                  <option value="">{t('products:sort.default')}</option>
                  <option value="name-asc">{t('products:sort.nameAsc')}</option>
                  <option value="name-desc">{t('products:sort.nameDesc')}</option>
                </select>
              </div>
            </div>

            {/* 移动端过滤器抽屉 */}
            {showFilters ? (
              <div className="mt-4 lg:hidden">
                {sidebar}
                <button
                  type="button"
                  onClick={() => setShowFilters(false)}
                  className="mt-3 w-full rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
                >
                  {t('products:filters.close')}
                </button>
              </div>
            ) : null}

            {filtered.length === 0 ? (
              <div className="mt-6 rounded-lg border border-slate-200 bg-white p-10 text-center text-slate-600">
                {t('products:filters.noResults')}
                <button type="button" onClick={clearAll} className="mt-3 block w-full font-semibold text-signal hover:underline">
                  {t('products:filters.noResultsClear')}
                </button>
              </div>
            ) : (
              <div className="mt-6 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {filtered.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
